import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Trophy, Sprout, Heart, Loader2, Medal } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../i18n';

const MEDAL = ['text-amber-400', 'text-slate-400', 'text-amber-700'];

export default function Leaderboard() {
    const navigate = useNavigate();
    const { t } = useI18n();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get('/leaderboard').then((r) => setData(r.data)).catch(() => setData({ top: [], me: null }))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="px-5 pt-10 pb-32 max-w-2xl mx-auto" data-testid="leaderboard-page">
            <button data-testid="lb-back" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/dashboard'))} className="inline-flex items-center gap-1 text-sm text-brand-ink3 hover:text-brand-ink mb-6"><ArrowLeft size={14} /> {t('common.back')}</button>
            <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">{t('lb.eyebrow')}</p>
            <h1 className="font-serif text-3xl sm:text-4xl text-brand-ink mt-1 mb-2 inline-flex items-center gap-2"><Trophy size={26} className="text-amber-400" /> {t('lb.title')}</h1>
            <p className="text-sm text-brand-ink2 mb-6">{t('lb.sub')}</p>

            {data?.me ? (
                <div className="bg-gradient-to-br from-brand-green to-brand-greenDark text-white rounded-3xl p-5 mb-6" data-testid="lb-me">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-[10px] uppercase tracking-widest font-semibold text-brand-bg/70">{t('lb.your_rank')}</div>
                            <div className="font-serif text-2xl mt-1">#{data.me.rank} · {data.me.points} {t('lb.points')}</div>
                        </div>
                        <div className="text-right text-xs text-brand-bg/85">
                            <div className="inline-flex items-center gap-1"><Sprout size={12} /> {data.me.finds} · {data.me.beds} 🌱</div>
                            <div className="inline-flex items-center gap-1 mt-1"><Heart size={12} /> {data.me.likes}</div>
                        </div>
                    </div>
                </div>
            ) : !loading && (
                <div className="bg-brand-card border border-brand-border rounded-2xl p-4 mb-6 text-sm text-brand-ink2" data-testid="lb-no-rank">{t('lb.no_rank')}</div>
            )}

            {loading ? (
                <div className="text-center py-16"><Loader2 className="animate-spin inline text-brand-ink3" /></div>
            ) : (data?.top?.length ? (
                <div className="space-y-2 stagger">
                    {data.top.map((e) => {
                        const isMe = data.me && e.rank === data.me.rank && e.name === data.me.name;
                        return (
                            <div key={e.rank} data-testid={`lb-row-${e.rank}`}
                                className={`flex items-center gap-3 rounded-2xl p-4 border ${isMe ? 'bg-brand-green/10 border-brand-green/40' : 'bg-brand-card border-brand-border'}`}>
                                <div className={`w-8 text-center font-serif text-lg ${e.rank <= 3 ? MEDAL[e.rank - 1] : 'text-brand-ink3'}`}>
                                    {e.rank <= 3 ? <Medal size={20} className="inline" /> : e.rank}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-serif text-base text-brand-ink truncate">{e.name}{isMe && <span className="text-xs text-brand-green ml-1">· {t('lb.you')}</span>}</div>
                                    <div className="text-[11px] text-brand-ink3 flex items-center gap-3 mt-0.5">
                                        <span className="inline-flex items-center gap-1"><Sprout size={11} /> {e.finds}</span>
                                        <span>🌱 {e.beds}</span>
                                        <span className="inline-flex items-center gap-1"><Heart size={11} /> {e.likes}</span>
                                    </div>
                                </div>
                                <div className="font-serif text-lg text-brand-ink">{e.points}<span className="text-[10px] text-brand-ink3 ml-1">{t('lb.points')}</span></div>
                            </div>
                        );
                    })}
                </div>
            ) : <p className="text-center text-brand-ink3 py-12 text-sm" data-testid="lb-empty">{t('lb.empty')}</p>)}

            <div className="mt-8 bg-brand-card border border-brand-border rounded-2xl p-4">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3 mb-2">{t('lb.how')}</div>
                <ul className="space-y-1.5 text-sm text-brand-ink2">
                    <li className="flex gap-2"><span className="text-brand-green">•</span> {t('lb.how_1')}</li>
                    <li className="flex gap-2"><span className="text-brand-green">•</span> {t('lb.how_2')}</li>
                    <li className="flex gap-2"><span className="text-brand-green">•</span> {t('lb.how_3')}</li>
                    <li className="flex gap-2"><span className="text-brand-green">•</span> {t('lb.how_4')}</li>
                </ul>
            </div>
        </div>
    );
}
